import React, { useState, useRef } from 'react'
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    ClickAwayListener,
    Checkbox,
    Portal,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckIcon from '@mui/icons-material/Check'

const CustomDropdownMenu = ({
    options = [],
    isOpen = false,
    anchorEl,
    onClose,
    onSelect,
    multiSelect = false,
    selectedOptions = multiSelect ? [] : null,
    anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
    transformOrigin = { vertical: 'top', horizontal: 'left' },
    width = '200px',
    maxHeight = '200px',
    dataAttributePrefix = '',
    dropdownId = '',
    proportionalPosition = null
}) => {
    const handleSelectOption = (option) => {
        if (multiSelect) {
            const newSelection = selectedOptions.includes(option.value) 
                ? selectedOptions.filter(val => val !== option.value)
                : [...selectedOptions, option.value]
            
            onSelect && onSelect(newSelection)
        } else {
            onSelect && onSelect(option.value)
            onClose && onClose()
        }
    }

    const isOptionSelected = (option) => {
        if (multiSelect) {
            return selectedOptions.includes(option.value)
        }
        return selectedOptions === option.value
    }

    const getDropdownPosition = () => {
        if (!anchorEl) return {}

        const anchorRect = anchorEl.getBoundingClientRect()
        const dropdownWidth = 280 // Fixed width for all dropdowns
        
        const baseStyle = {
            position: 'fixed',
            zIndex: 1300,
            maxHeight,
            overflowY: 'auto',
            border: '1px solid #E5E7EB',
            borderRadius: '6px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
            width: `${dropdownWidth}px`,
        }

        // Use proportional positioning if provided
        if (proportionalPosition && anchorEl) {
            const { coveragePercent, preferredVertical, horizontalOffset = null } = proportionalPosition
            
            // Calculate actual dropdown height based on options
            const itemHeight = 36 // Height per list item (py: '8px' = 16px + minHeight: '36px' - padding)
            const padding = 8 // List padding (py: '4px' = 8px total)
            const optionCount = Math.max(options.length, 1) // Always account for at least one option
            const calculatedHeight = Math.min(
                (optionCount * itemHeight) + padding,
                parseInt(maxHeight) || 200
            )
            
            // Calculate how much of the cell width to cover (proportional)
            const cellWidth = anchorRect.width
            const coverageWidth = (cellWidth * coveragePercent) / 100
            const actualWidth = Math.max(dropdownWidth, coverageWidth)
            
            // Calculate horizontal position based on horizontalOffset or center by default
            let leftOffset
            if (horizontalOffset !== null) {
                // Use explicit horizontal offset (0 = leading edge, 100 = trailing edge)
                leftOffset = anchorRect.left + (cellWidth * horizontalOffset / 100)
            } else {
                // Default: center the dropdown over the covered area
                leftOffset = anchorRect.left + (cellWidth - actualWidth) / 2
            }
            
            // Get viewport bounds for collision detection
            const viewportHeight = window.innerHeight
            const spaceBelow = viewportHeight - anchorRect.bottom
            const spaceAbove = anchorRect.top
            
            // Intelligent vertical positioning based on available space
            let topOffset
            let actualPreferredVertical = preferredVertical
            
            // Override preferred direction if there's not enough space
            if (preferredVertical === 'top' && spaceBelow < calculatedHeight && spaceAbove > calculatedHeight) {
                actualPreferredVertical = 'bottom' // Flip to show above
            } else if (preferredVertical === 'bottom' && spaceAbove < calculatedHeight && spaceBelow > calculatedHeight) {
                actualPreferredVertical = 'top' // Flip to show below
            }
            
            if (actualPreferredVertical === 'top') {
                // Extend downward from top of cell
                topOffset = anchorRect.top
            } else {
                // Extend upward from bottom of cell  
                topOffset = anchorRect.bottom - calculatedHeight
            }
            
            // Ensure dropdown stays within viewport bounds
            if (topOffset + calculatedHeight > viewportHeight) {
                topOffset = viewportHeight - calculatedHeight - 10
            }
            if (topOffset < 0) {
                topOffset = 10
            }

            return {
                ...baseStyle,
                left: `${leftOffset}px`,
                top: `${topOffset}px`,
                width: `${actualWidth}px`,
                maxHeight: `${calculatedHeight}px`,
            }
        }

        // Fallback to original positioning logic
        let left = anchorRect.left
        let top = anchorRect.bottom + 4

        if (anchorOrigin.vertical === 'bottom') {
            top = anchorRect.top - parseInt(maxHeight) - 4
        } else if (anchorOrigin.vertical === 'center') {
            top = anchorRect.top + (anchorRect.height - parseInt(maxHeight)) / 2
        }

        if (anchorOrigin.horizontal === 'right') {
            left = anchorRect.right - dropdownWidth
        } else if (anchorOrigin.horizontal === 'center') {
            left = anchorRect.left + (anchorRect.width - dropdownWidth) / 2
        }

        return {
            ...baseStyle,
            left: `${left}px`,
            top: `${top}px`,
        }
    }

    if (!isOpen || !anchorEl) return null

    return (
        <Portal>
            <ClickAwayListener onClickAway={onClose}>
                <Paper
                    elevation={8}
                    sx={getDropdownPosition()}
                >
                <List 
                    id={dropdownId}
                    sx={{ 
                        py: '4px',
                        maxHeight: 'inherit',
                        overflowY: 'auto'
                    }}
                >
                    {options.map((option) => (
                        <ListItem
                            key={option.value}
                            onClick={() => handleSelectOption(option)}
                            {...(dataAttributePrefix && { [`data-${dataAttributePrefix}-option`]: option.value })}
                            sx={{
                                py: '8px',
                                px: '15px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: '36px',
                                '&:hover': {
                                    backgroundColor: '#F9FAFB',
                                },
                                backgroundColor: isOptionSelected(option) ? '#EEF2FF' : 'transparent',
                            }}
                        >
                            {multiSelect && (
                                <Checkbox
                                    checked={isOptionSelected(option)}
                                    sx={{
                                        padding: '0px',
                                        marginRight: '12px',
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '18px',
                                        },
                                    }}
                                />
                            )}
                            <ListItemText
                                primary={option.label}
                                sx={{
                                    margin: 0,
                                    '& .MuiListItemText-primary': {
                                        fontSize: '14px',
                                        fontWeight: 'medium',
                                        color: '#374151',
                                        lineHeight: '16px',
                                    },
                                }}
                            />
                            {!multiSelect && isOptionSelected(option) && (
                                <CheckIcon
                                    sx={{
                                        fontSize: '16px',
                                        color: '#4338CA',
                                        ml: 'auto'
                                    }}
                                />
                            )}
                        </ListItem>
                    ))}
                    {options.length === 0 && (
                        <ListItem sx={{ py: '12px', px: '15px' }}>
                            <ListItemText
                                primary="No options available"
                                sx={{
                                    margin: 0,
                                    '& .MuiListItemText-primary': {
                                        fontSize: '14px',
                                        color: '#9CA3AF',
                                        fontStyle: 'italic',
                                        textAlign: 'center',
                                    },
                                }}
                            />
                        </ListItem>
                    )}
                </List>
            </Paper>
        </ClickAwayListener>
        </Portal>
    )
}

const CustomSelect = ({ 
    options = [], 
    placeholder = "Select options",
    onSelect,
    multiSelect = false,
    disabled = false,
    width = '200px',
    anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
    transformOrigin = { vertical: 'top', horizontal: 'left' }
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const [selectedOptions, setSelectedOptions] = useState(multiSelect ? [] : null)
    const anchorRef = useRef(null)

    const handleToggleDropdown = () => {
        if (!disabled) {
            setIsOpen(!isOpen)
        }
    }

    const handleSelectOption = (option) => {
        if (multiSelect) {
            const newSelection = selectedOptions.includes(option.value) 
                ? selectedOptions.filter(val => val !== option.value)
                : [...selectedOptions, option.value]
            
            setSelectedOptions(newSelection)
            onSelect && onSelect(newSelection)
        } else {
            setSelectedOptions(option.value)
            setIsOpen(false)
            onSelect && onSelect(option.value)
        }
    }

    const handleClickAway = () => {
        setIsOpen(false)
    }

    const getDropdownPosition = () => {
        const baseStyle = {
            position: 'absolute',
            zIndex: 1300,
            mt: '4px',
            maxHeight: '200px',
            overflowY: 'auto',
            border: '1px solid #E5E7EB',
            borderRadius: '6px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
        }

        const positionStyle = {}
        
        // Handle vertical positioning
        if (anchorOrigin.vertical === 'bottom') {
            positionStyle.top = '100%'
            positionStyle.mt = '4px'
        } else if (anchorOrigin.vertical === 'top') {
            positionStyle.bottom = '100%'
            positionStyle.mb = '4px'
        } else if (anchorOrigin.vertical === 'center') {
            positionStyle.top = '50%'
            positionStyle.transform = 'translateY(-50%)'
        }

        // Handle horizontal positioning
        if (anchorOrigin.horizontal === 'left') {
            positionStyle.left = 0
            positionStyle.right = 0
        } else if (anchorOrigin.horizontal === 'right') {
            positionStyle.right = 0
            positionStyle.left = 'auto'
            positionStyle.minWidth = width
        } else if (anchorOrigin.horizontal === 'center') {
            positionStyle.left = '50%'
            positionStyle.transform = positionStyle.transform 
                ? `${positionStyle.transform} translateX(-50%)`
                : 'translateX(-50%)'
            positionStyle.minWidth = width
        }

        return { ...baseStyle, ...positionStyle }
    }

    const getDisplayText = () => {
        if (multiSelect) {
            if (selectedOptions.length === 0) return placeholder
            if (selectedOptions.length === 1) {
                const option = options.find(opt => opt.value === selectedOptions[0])
                return option ? option.label : placeholder
            }
            return `${selectedOptions.length} selected`
        } else {
            if (!selectedOptions) return placeholder
            const option = options.find(opt => opt.value === selectedOptions)
            return option ? option.label : placeholder
        }
    }

    const isOptionSelected = (option) => {
        if (multiSelect) {
            return selectedOptions.includes(option.value)
        }
        return selectedOptions === option.value
    }

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <Box sx={{ position: 'relative', width }}>
                <Box
                    ref={anchorRef}
                    onClick={handleToggleDropdown}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '9px 15px',
                        backgroundColor: disabled ? '#F3F4F6' : '#FFFFFF',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        minHeight: '38px',
                        '&:hover': {
                            backgroundColor: disabled ? '#F3F4F6' : '#F9FAFB',
                        },
                    }}
                >
                    <Typography 
                        sx={{ 
                            fontSize: '14px', 
                            fontWeight: 'medium', 
                            color: disabled ? '#9CA3AF' : (selectedOptions && (multiSelect ? selectedOptions.length > 0 : true)) ? '#374151' : '#9CA3AF',
                            flex: 1,
                            textAlign: 'left'
                        }}
                    >
                        {getDisplayText()}
                    </Typography>
                </Box>

                {isOpen && !disabled && (
                    <Paper
                        elevation={8}
                        sx={getDropdownPosition()}
                    >
                        <List sx={{ py: '4px' }}>
                            {options.map((option) => (
                                <ListItem
                                    key={option.value}
                                    onClick={() => handleSelectOption(option)}
                                    sx={{
                                        py: '8px',
                                        px: '15px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        minHeight: '36px',
                                        '&:hover': {
                                            backgroundColor: '#F9FAFB',
                                        },
                                        backgroundColor: isOptionSelected(option) ? '#EEF2FF' : 'transparent',
                                    }}
                                >
                                    {multiSelect && (
                                        <Checkbox
                                            checked={isOptionSelected(option)}
                                            sx={{
                                                padding: '0px',
                                                marginRight: '12px',
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '18px',
                                                },
                                            }}
                                        />
                                    )}
                                    <ListItemText
                                        primary={option.label}
                                        sx={{
                                            margin: 0,
                                            '& .MuiListItemText-primary': {
                                                fontSize: '14px',
                                                fontWeight: 'medium',
                                                color: '#374151',
                                                lineHeight: '16px',
                                            },
                                        }}
                                    />
                                    {!multiSelect && isOptionSelected(option) && (
                                        <CheckIcon
                                            sx={{
                                                fontSize: '16px',
                                                color: '#4338CA',
                                                ml: 'auto'
                                            }}
                                        />
                                    )}
                                </ListItem>
                            ))}
                            {options.length === 0 && (
                                <ListItem sx={{ py: '12px', px: '15px' }}>
                                    <ListItemText
                                        primary="No options available"
                                        sx={{
                                            margin: 0,
                                            '& .MuiListItemText-primary': {
                                                fontSize: '14px',
                                                color: '#9CA3AF',
                                                fontStyle: 'italic',
                                                textAlign: 'center',
                                            },
                                        }}
                                    />
                                </ListItem>
                            )}
                        </List>
                    </Paper>
                )}
            </Box>
        </ClickAwayListener>
    )
}

export default CustomSelect
export { CustomDropdownMenu }

/*
USAGE EXAMPLES:

1. Basic CustomSelect with default bottom-left positioning:
```jsx
<CustomSelect
    options={[
        { value: 'option1', label: 'Option 1' },
        { value: 'option2', label: 'Option 2' }
    ]}
    onSelect={(value) => console.log(value)}
    placeholder="Choose an option"
/>
```

2. CustomSelect with top-right positioning:
```jsx
<CustomSelect
    options={options}
    onSelect={handleSelect}
    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
    transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    placeholder="Top Right Menu"
/>
```

3. CustomSelect with center positioning:
```jsx
<CustomSelect
    options={options}
    onSelect={handleSelect}
    anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
    transformOrigin={{ vertical: 'center', horizontal: 'center' }}
    placeholder="Centered Menu"
/>
```

4. Multi-select with bottom-right positioning:
```jsx
<CustomSelect
    options={options}
    onSelect={handleMultiSelect}
    multiSelect={true}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    placeholder="Multi-select (Bottom Right)"
/>
```

5. Standalone CustomDropdownMenu with button control:
```jsx
function ExampleWithStandaloneMenu() {
    const [isOpen, setIsOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)
    const [selected, setSelected] = useState(null)
    
    const handleButtonClick = (event) => {
        setAnchorEl(event.currentTarget)
        setIsOpen(true)
    }
    
    const handleClose = () => {
        setIsOpen(false)
        setAnchorEl(null)
    }
    
    const handleSelect = (value) => {
        setSelected(value)
        handleClose()
    }
    
    return (
        <div>
            <Button onClick={handleButtonClick}>
                {selected ? `Selected: ${selected}` : 'Click to open menu'}
            </Button>
            <CustomDropdownMenu
                isOpen={isOpen}
                anchorEl={anchorEl}
                onClose={handleClose}
                onSelect={handleSelect}
                options={[
                    { value: 'action1', label: 'Action 1' },
                    { value: 'action2', label: 'Action 2' },
                    { value: 'action3', label: 'Action 3' }
                ]}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                width="150px"
            />
        </div>
    )
}
```

6. Standalone menu with top positioning:
```jsx
<CustomDropdownMenu
    isOpen={menuOpen}
    anchorEl={buttonRef.current}
    onClose={() => setMenuOpen(false)}
    onSelect={handleMenuSelect}
    options={menuOptions}
    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    width="200px"
    maxHeight="150px"
/>
```

7. Multi-select standalone menu:
```jsx
function MultiSelectMenu() {
    const [isOpen, setIsOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedItems, setSelectedItems] = useState([])
    
    return (
        <div>
            <Button onClick={(e) => { setAnchorEl(e.currentTarget); setIsOpen(true) }}>
                {selectedItems.length} items selected
            </Button>
            <CustomDropdownMenu
                isOpen={isOpen}
                anchorEl={anchorEl}
                onClose={() => setIsOpen(false)}
                onSelect={setSelectedItems}
                selectedOptions={selectedItems}
                multiSelect={true}
                options={[
                    { value: 'item1', label: 'Item 1' },
                    { value: 'item2', label: 'Item 2' },
                    { value: 'item3', label: 'Item 3' }
                ]}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                width="180px"
            />
        </div>
    )
}
```

ANCHOR POSITIONING OPTIONS:

anchorOrigin.vertical:
- 'top': Menu appears above the anchor element
- 'bottom': Menu appears below the anchor element (default)
- 'center': Menu is vertically centered on the anchor element

anchorOrigin.horizontal:
- 'left': Menu aligns to the left edge of anchor (default)
- 'right': Menu aligns to the right edge of anchor
- 'center': Menu is horizontally centered on the anchor element

transformOrigin (for future animation support):
- Controls the transform origin point for animations
- Same options as anchorOrigin

PROPS:

CustomSelect Props:
- options: Array of { value, label } objects
- placeholder: String to display when no selection
- onSelect: Callback function when option is selected
- multiSelect: Boolean for multi-selection mode
- disabled: Boolean to disable the component
- width: CSS width value (default: '200px')
- anchorOrigin: Object with vertical/horizontal positioning
- transformOrigin: Object for animation transform origin

CustomDropdownMenu Props:
- options: Array of { value, label } objects
- isOpen: Boolean controlling menu visibility
- anchorEl: DOM element reference for positioning
- onClose: Callback when menu should close
- onSelect: Callback when option is selected
- multiSelect: Boolean for multi-selection mode
- selectedOptions: Current selection(s)
- anchorOrigin: Positioning configuration
- transformOrigin: Transform origin configuration
- width: Menu width (default: '200px')
- maxHeight: Maximum menu height (default: '200px')
*/